<template>
  <div class="space-y-8">
    <!-- Head Contract Settings Card -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div class="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-gray-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

            <h3 class="text-lg font-semibold text-gray-900">
              {{ $t('ProjectSettings.HeadContractTab.header.headContractSettings') }}
            </h3>

            <AppInfoHover
              :use-background="false"
            >
              <div class="bg-white rounded-lg shadow-md p-4 border border-gray-200 text-sm font-medium text-gray-700">
                <p class="mb-1">
                  {{ $t('ProjectSettings.HeadContractTab.contractInfoHover.description') }}
                </p>

                <ul class="list-disc list-inside font-normal">
                  <li>
                    {{ $t('ProjectSettings.HeadContractTab.contractInfoHover.contractStartDate') }}
                  </li>

                  <li>
                    {{ $t('ProjectSettings.HeadContractTab.contractInfoHover.contractEndDate') }}
                  </li>

                  <li>
                    {{ $t('ProjectSettings.HeadContractTab.contractInfoHover.claimStartDate') }}
                  </li>

                  <li v-if="project?.isContractor">
                    {{ $t('ProjectSettings.HeadContractTab.contractInfoHover.superintendentEmail') }}
                  </li>
                </ul>
              </div>
            </AppInfoHover>
          </div>

          <div class="flex items-center space-x-2">
            <button
              v-if="isProjectCompleted"
              type="button"
              class="btn btn--primary-blue flex items-center space-x-2"
              data-test="export-retention-release-certificate"
              :disabled="exportRetentionReleaseAction.is(States.LOADING)"
              @click="exportRetentionRelease"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-4 h-4">
<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

              <span>
                {{ $t('ProjectSettings.HeadContractTab.button.exportRetentionRelease') }}
              </span>
            </button>

            <button
              v-if="project?.unarchived"
              type="button"
              class="btn btn--primary-blue flex items-center space-x-2"
              data-test="save-head-contract"
              :disabled="updateProjectAction.is(States.LOADING)"
              @click="updateProject"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
</svg>

              <span>
                {{ $t('ProjectSettings.HeadContractTab.button.saveHeadContractSettings') }}
              </span>
            </button>
          </div>
        </div>
      </div>

      <div class="p-6">
        <!-- Contract Configuration Section -->
        <div
          v-if="userStore.isRole(['admin', 'project_admin', 'company_admin', 'contract_admin'])"
          class="space-y-8"
        >
          <form
            method="post"
            @submit.prevent="updateProject"
          >
            <div class="flex gap-6">
              <!-- Contract Details -->
              <div class="space-y-4 w-1/4">
                <h5 class="text-sm font-medium text-gray-900 flex items-center space-x-2">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-4 h-4 text-gray-500">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

                  <span>Contract Details</span>
                </h5>

                <div class="space-y-4">
                  <template v-if="!project.feature_flag_progress_claims_lite">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">
                        {{ $t('ProjectSettings.HeadContractTab.label.contractorEmail') }}
                      </label>

                      <AppInput
                        v-model="projectForm.form.contractor_email"
                        :error="projectForm.getErrors('contractor_email')"
                        class="form-input"
                        type="text"
                        name="contractor_email"
                      />
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">
                        {{ $t('ProjectSettings.HeadContractTab.label.pqsEmail') }}
                      </label>

                      <AppInput
                        v-model="projectForm.form.pqs_email"
                        :error="projectForm.getErrors('pqs_email')"
                        class="form-input"
                        type="text"
                        name="pqs_email"
                      />
                    </div>
                  </template>

                  <div
                    v-if="project.isContractor"
                  >
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      {{ $t('ProjectSettings.HeadContractTab.label.superintendentEmail') }}
                      <span class="text-xs text-gray-500">(Receives notices for variations and delays)</span>
                    </label>

                    <AppInput
                      v-model="projectForm.form.superintendent_email"
                      :error="projectForm.getErrors('superintendent_email')"
                      class="form-input"
                      type="text"
                      name="superintendent_email"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      {{ $t('ProjectSettings.HeadContractTab.label.contractStartDate') }}
                    </label>

                    <AppInput
                      v-model="projectForm.form.contract_start_date"
                      :error="projectForm.getErrors('contract_start_date')"
                      class="form-input"
                      type="date"
                      name="contract_start_date"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      {{ $t('ProjectSettings.HeadContractTab.label.contractEndDate') }}
                    </label>

                    <AppInput
                      v-model="projectForm.form.contract_end_date"
                      :error="projectForm.getErrors('contract_end_date')"
                      class="form-input"
                      type="date"
                      name="contract_end_date"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      {{ $t('ProjectSettings.HeadContractTab.label.claimStartDate') }}
                    </label>

                    <AppInput
                      v-model="projectForm.form.claim_start"
                      :error="projectForm.getErrors('claim_start')"
                      class="form-input"
                      type="date"
                      name="claim_start"
                    />
                  </div>

                  <div
                    v-if="contractLength"
                    class="p-3 bg-blue-50 rounded-lg border border-blue-200"
                  >
                    <p class="text-sm font-medium text-blue-900">
                      {{ $t('ProjectSettings.HeadContractTab.projectLength', { contractLength }) }}
                    </p>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      {{ $t('ProjectSettings.HeadContractTab.label.commentaryLock') }}
                      <span class="text-xs text-gray-500">({{ $t('ProjectSettings.HeadContractTab.hint.daysBeforeEndOfMonth') }})</span>
                    </label>

                    <AppInput
                      v-model="projectForm.form.commentary_lock"
                      :error="projectForm.getErrors('commentary_lock')"
                      class="form-input"
                      type="number"
                      name="commentary_lock"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Claim Margin Percentage
                    </label>

                    <AppInput
                      v-model="projectForm.form.claim_margin_percent"
                      :error="projectForm.getErrors('claim_margin_percent')"
                      class="form-input"
                      type="number"
                      name="claim_margin_percent"
                      min="0"
                      max="100"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Performance Security
                    </label>

                    <div class="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        class="btn text-sm py-3"
                        :class="[projectForm.form.performance_security === 'retention' ? 'btn--primary-blue' : 'btn--secondary-blue']"
                        @click="projectForm.form.performance_security = 'retention'"
                      >
                        Retention
                      </button>

                      <button
                        type="button"
                        class="btn text-sm py-3"
                        :class="[projectForm.form.performance_security === 'bank_guarantee' ? 'btn--primary-blue' : 'btn--secondary-blue']"
                        @click="projectForm.form.performance_security = 'bank_guarantee'"
                      >
                        Bank Guarantee
                      </button>
                    </div>

                    <div
                      v-if="projectForm.form.performance_security === 'retention'"
                      class="space-y-4 pt-2"
                    >
                      <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                          {{ $t('ProjectSettings.HeadContractTab.label.claimRetentionPercent') }}
                          <span class="text-xs text-gray-500">(maximum amount per claim)</span>
                        </label>

                        <AppInput
                          v-model="projectForm.form.claim_retention_percent"
                          :error="projectForm.getErrors('claim_retention_percent')"
                          class="form-input"
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          name="claim_retention_percent"
                        />
                      </div>

                      <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                          {{ $t('ProjectSettings.HeadContractTab.label.totalRetentionPercent') }}
                          <span class="text-xs text-gray-500">(maximum total of contract sum)</span>
                        </label>

                        <AppInput
                          v-model="projectForm.form.total_retention_percent"
                          :error="projectForm.getErrors('total_retention_percent')"
                          class="form-input"
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          name="total_retention_percent"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                    <span class="text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.allowCertifiedOverBudget') }}</span>

                    <input
                      v-model="projectForm.form.allow_certified_over_budget"
                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      type="checkbox"
                      name="allow_certified_over_budget"
                    >
                  </div>
                </div>
              </div>

              <!-- Client Emails -->
              <div class="space-y-4 w-1/4">
                <h5 class="text-sm font-medium text-gray-900 flex items-center space-x-2">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-gray-500">
    <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
</svg>

                  <div>
                    <span>{{ $t('ProjectSettings.HeadContractTab.label.emailAddress') }}</span>

                    <p class="text-xs text-gray-500">
                      (Receives submissions and approvals for variations and delays)
                    </p>
                  </div>
                </h5>

                <div class="space-y-4">
                  <div class="flex space-x-2 items-center">
                    <div class="flex-1">
                      <AppInput
                        v-model="clientEmailInput"
                        type="email"
                        name="client_email_input"
                        class="form-input"
                        placeholder="Enter client email..."
                        :error="projectForm.getErrors('client_emails')"
                      />
                    </div>

                    <button
                      type="button"
                      class="btn btn--secondary-blue flex items-center space-x-1 text-sm"
                      :disabled="!canAddClientEmail"
                      @click="addClientEmail"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

                      <span>{{ $t('ProjectSettings.HeadContractTab.button.addEmail') }}</span>
                    </button>
                  </div>

                  <div class="space-y-2 max-h-48 overflow-y-auto">
                    <div
                      v-if="projectForm.form.client_emails?.length"
                      class="space-y-2"
                    >
                      <div
                        v-for="(email, i) in projectForm.form.client_emails"
                        :key="i"
                        class="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
                      >
                        <span class="text-sm text-gray-900">{{ email }}</span>

                        <button
                          type="button"
                          class="btn-icon btn-icon-red btn-icon--bg-red transition-colors"
                          @click="removeClientEmail(i)"
                        >
                          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
                        </button>
                      </div>
                    </div>

                    <div
                      v-else
                      class="text-center py-6 text-gray-500"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="mx-auto h-8 w-8 text-gray-400 mb-2">
    <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
</svg>

                      <p class="text-sm">
                        {{ $t('ProjectSettings.HeadContractTab.noClientEmails') }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Observed Holidays -->
              <div class="space-y-4 w-1/2">
                <h5 class="text-sm font-medium text-gray-900 flex items-center space-x-2">
                  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4 text-gray-500">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
</svg>

                  <span>{{ $t('ProjectSettings.HeadContractTab.label.observedHolidays') }}</span>
                </h5>

                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <!-- Add Holiday Form -->
                  <div class="space-y-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">
                        {{ $t('ProjectSettings.HeadContractTab.label.holidayName') }}
                      </label>

                      <AppInput
                        v-model="holidayLabel"
                        type="text"
                        name="holiday_label"
                        class="form-input"
                        placeholder="Enter holiday name..."
                      />
                    </div>

                    <!-- Holiday Type Toggle -->
                    <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span class="text-sm font-medium text-gray-700">Holiday Type</span>

                      <div class="flex space-x-2">
                        <button
                          type="button"
                          class="px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
                          :class="[holidayType === 'single' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300']"
                          @click="holidayType = 'single'"
                        >
                          Single Day
                        </button>

                        <button
                          type="button"
                          class="px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
                          :class="[holidayType === 'range' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300']"
                          @click="holidayType = 'range'"
                        >
                          Date Range
                        </button>
                      </div>
                    </div>

                    <!-- Single Day Holiday Fields -->
                    <template v-if="holidayType === 'single'">
                      <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span class="text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.holidayOccursEveryYear') }}</span>

                        <input
                          v-model="holidayRecurring"
                          type="checkbox"
                          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        >
                      </div>

                      <div class="grid grid-cols-2 gap-3">
                        <div>
                          <label class="block text-sm font-medium text-gray-700 mb-2">
                            {{ $t('ProjectSettings.HeadContractTab.label.day') }}
                          </label>

                          <AppSelect
                            v-model.number="holidayDay"
                            :options="Array(31).fill(0).map((e, i) => i+1)"
                            name="holiday_day"
                            class="form-input"
                          />
                        </div>

                        <div>
                          <label class="block text-sm font-medium text-gray-700 mb-2">
                            {{ $t('ProjectSettings.HeadContractTab.label.month') }}
                          </label>

                          <AppSelect
                            v-model.number="holidayMonth"
                            :options="Array(12).fill(0).map((e, i) => i).map((month) => ({
                              label: MONTHS[month],
                              value: month + 1
                            }))"
                            name="holiday_month"
                            class="form-input"
                          />
                        </div>
                      </div>

                      <div v-if="!holidayRecurring">
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                          {{ $t('ProjectSettings.HeadContractTab.label.year') }}
                        </label>

                        <AppInput
                          v-model.number="holidayYear"
                          type="text"
                          name="holiday_year"
                          class="form-input"
                          placeholder="Enter year..."
                        />
                      </div>
                    </template>

                    <!-- Range Holiday Fields -->
                    <template v-else>
                      <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                          Start Date
                        </label>

                        <AppInput
                          v-model="holidayStartDate"
                          type="date"
                          name="holiday_start_date"
                          class="form-input"
                        />
                      </div>

                      <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                          End Date
                        </label>

                        <AppInput
                          v-model="holidayEndDate"
                          type="date"
                          name="holiday_end_date"
                          class="form-input"
                        />
                      </div>
                    </template>

                    <button
                      type="button"
                      class="btn btn--secondary-blue w-full flex items-center justify-center space-x-2"
                      :disabled="!canAddHoliday"
                      @click="addHoliday"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

                      <span>{{ $t('ProjectSettings.HeadContractTab.button.addHoliday') }}</span>
                    </button>
                  </div>

                  <!-- Holidays List -->
                  <div class="space-y-2 max-h-64 overflow-y-auto">
                    <div
                      v-if="projectForm.form.holidays?.length"
                      class="space-y-2"
                    >
                      <div
                        v-for="(holiday, i) in projectForm.form.holidays"
                        :key="i"
                        class="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
                      >
                        <div class="text-sm text-gray-900">
                          <template v-if="holiday.type === 'range' || (holiday.start_date && holiday.end_date)">
                            <strong>{{ holiday.label }}</strong>: {{ DateTime.fromISO(holiday.start_date).toLocaleString() }} - {{ DateTime.fromISO(holiday.end_date).toLocaleString() }}
                          </template>

                          <template v-else>
                            <i18n-t
                              v-if="holiday.year"
                              keypath="ProjectSettings.HeadContractTab.holidayOccursOn"
                              tag="p"
                            >
                              <template #holiday>
                                <strong>{{ holiday.label }}</strong>
                              </template>

                              <template #date>
                                <span>{{
                                  DateTime.fromObject({
                                    day: holiday.day, month: holiday.month, year: holiday.year
                                  }).toLocaleString()
                                }}</span>
                              </template>
                            </i18n-t>

                            <i18n-t
                              v-else
                              keypath="ProjectSettings.HeadContractTab.holidayOccursEveryYear"
                              tag="p"
                            >
                              <template #holiday>
                                <strong>{{ holiday.label }}</strong>
                              </template>

                              <template #date>
                                <span>{{
                                  DateTime.fromObject({ day: holiday.day, month: holiday.month }).toLocaleString()
                                }}</span>
                              </template>
                            </i18n-t>
                          </template>
                        </div>

                        <button
                          type="button"
                          class="btn-icon btn-icon-red btn-icon--bg-red transition-colors"
                          @click="removeHoliday(i)"
                        >
                          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
                        </button>
                      </div>
                    </div>

                    <div
                      v-else
                      class="text-center py-6 text-gray-500"
                    >
                      <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="mx-auto h-8 w-8 text-gray-400 mb-2">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
</svg>

                      <p class="text-sm">
                        {{ $t('ProjectSettings.HeadContractTab.noHolidaysAdded') }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Financial Report Details Card -->
    <div
      v-if="userStore.isRole(['admin', 'project_admin', 'company_admin', 'contract_admin'])"
      class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
    >
      <div class="from-gray-50 to-gray-100 bg-gradient-to-r px-6 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <div class="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
              <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-5 h-5 text-gray-600">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
    />
</svg>
            </div>

            <h3 class="text-lg font-semibold text-gray-900">
              {{ $t('ProjectSettings.HeadContractTab.financialReportDetails') }}
            </h3>
          </div>
        </div>
      </div>

      <div class="p-6">
        <form
          method="post"
          class="space-y-6"
          @submit.prevent="updateProject"
        >
          <!-- Details Grid -->
          <div class="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">
                {{ $t('ProjectSettings.HeadContractTab.label.clientDetails') }}
              </label>

              <AppTextArea
                v-model="projectForm.form.client_details"
                :error="projectForm.getErrors('client_details')"
                class="form-input"
                type="text"
                name="client_details"
                rows="5"
              />
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">
                {{ project.isClient ? $t('ProjectSettings.HeadContractTab.label.contractorDetails') : $t('ProjectSettings.HeadContractTab.label.quantitySurveyorDetails') }}
              </label>

              <AppTextArea
                v-model="projectForm.form.hired_company_details"
                :error="projectForm.getErrors('hired_company_details')"
                class="form-input"
                type="text"
                name="hired_company_details"
                rows="5"
              />
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">
                {{ $t('ProjectSettings.HeadContractTab.label.financierDetails') }}
              </label>

              <AppTextArea
                v-model="projectForm.form.financier_details"
                :error="projectForm.getErrors('financier_details')"
                class="form-input"
                type="text"
                name="financier_details"
                rows="5"
              />
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">
                {{ $t('ProjectSettings.HeadContractTab.label.siteDetails') }}
              </label>

              <AppTextArea
                v-model="projectForm.form.site_details"
                :error="projectForm.getErrors('site_details')"
                class="form-input"
                type="text"
                name="site_details"
                rows="5"
              />
            </div>
          </div>

          <!-- Tax Options -->
          <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div class="flex items-center space-x-2 mb-4">
              <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-5 h-5 text-gray-600">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
    />
</svg>

              <h4 class="text-sm font-medium text-gray-900">
                Tax Options
              </h4>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                <span class="text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.showSeparateTaxLineOnReport') }}</span>

                <input
                  v-model="projectForm.form.apply_tax"
                  class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  type="checkbox"
                  name="apply_tax"
                >
              </div>

              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">
                  {{ $t('ProjectSettings.HeadContractTab.label.taxRate') }} (%)
                </label>

                <AppInput
                  v-model="projectForm.form.tax_percent"
                  :error="projectForm.getErrors('tax_percent')"
                  class="form-input"
                  type="text"
                  name="tax_percent"
                />
              </div>
            </div>
          </div>

          <!-- Claim Options -->
          <!-- <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div class="flex items-center space-x-2 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-gray-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

              <h4 class="text-sm font-medium text-gray-900">
                Claim Options
              </h4>
            </div>

            <div class="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
              <span class="text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.showVeItemsOnClaims') }}</span>

              <input
                v-model="projectForm.form.show_ve_items_on_claims"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                type="checkbox"
                name="show_ve_items_on_claims"
              >
            </div>
          </div> -->

          <!-- Clauses Section -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div class="space-y-2">
              <div class="flex items-center space-x-2">
                <label class="block text-sm font-medium text-gray-700">
                  {{ $t('ProjectSettings.HeadContractTab.label.claimIntroductionClause') }}
                </label>

                <AppInfoHover tooltip="Appears above the claim totals. You may insert ':date' and it will be replaced with the date that the claim was certified." />
              </div>

              <AppTextArea
                v-model="projectForm.form.claim_clause"
                :error="projectForm.getErrors('claim_clause')"
                class="form-input"
                type="text"
                name="claim_clause"
                rows="5"
              />
            </div>

            <div class="space-y-2">
              <div class="flex items-center space-x-2">
                <label class="block text-sm font-medium text-gray-700">
                  {{ $t('ProjectSettings.HeadContractTab.label.claimCertificateClause') }}
                </label>

                <AppInfoHover tooltip="Appears at the bottom of the first page." />
              </div>

              <AppTextArea
                v-model="projectForm.form.certificate_clause"
                :error="projectForm.getErrors('certificate_clause')"
                class="form-input"
                type="text"
                name="certificate_clause"
                rows="5"
              />
            </div>

            <div class="space-y-2">
              <div class="flex items-center space-x-2">
                <label class="block text-sm font-medium text-gray-700">
                  {{ $t('ProjectSettings.HeadContractTab.label.finalAccountClause') }}
                </label>

                <AppInfoHover tooltip="Appears at the bottom of the first page of the Statement of Final Account." />
              </div>

              <AppTextArea
                v-model="projectForm.form.final_account_clause"
                :error="projectForm.getErrors('final_account_clause')"
                class="form-input"
                type="text"
                name="final_account_clause"
                rows="5"
              />
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Notice Cards Section -->
    <div
      v-if="userStore.isRole(['admin', 'project_admin', 'company_admin', 'contract_admin', 'contract_manager'])"
      class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-6"
    >
      <!-- Notice of Variation Card -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div class="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3">
          <div class="flex items-center space-x-2">
            <div class="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-4 h-4 text-gray-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
            </div>

            <h4 class="text-sm font-semibold text-gray-900">
              {{ $t('ProjectSettings.HeadContractTab.noticeOfVariation') }}
            </h4>
          </div>
        </div>

        <div class="p-4">
          <form
            method="post"
            class="space-y-4"
            @submit.prevent="updateProject"
          >
            <div class="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <span class="text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.noticeRequired') }}</span>

              <input
                v-model="projectForm.form.nov_required"
                name="nov_required"
                type="checkbox"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              >
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">
                {{ $t('ProjectSettings.HeadContractTab.label.noticePeriod') }} ({{ $t('ProjectSettings.HeadContractTab.hint.days') }})
              </label>

              <AppInput
                v-model="projectForm.form.nov_period"
                class="form-input"
                type="number"
                name="nov_period"
                :error="projectForm.getErrors('nov_period')"
              />
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.daysType') }}</label>

              <div class="flex space-x-2">
                <button
                  type="button"
                  class="flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors"
                  :class="[projectForm.form.nov_business_days ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
                  @click="projectForm.form.nov_business_days = true"
                >
                  {{ $t('ProjectSettings.HeadContractTab.button.business') }}
                </button>

                <button
                  type="button"
                  class="flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors"
                  :class="[!projectForm.form.nov_business_days ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
                  @click="projectForm.form.nov_business_days = false"
                >
                  {{ $t('ProjectSettings.HeadContractTab.button.calendar') }}
                </button>
              </div>
            </div>

            <div
              v-if="project.isContractor"
              class="space-y-2"
            >
              <label class="block text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.noticeClause') }}</label>

              <AppTextArea
                v-model="projectForm.form.nov_clause"
                name="nov_clause"
                class="form-input resize-none"
                :error="projectForm.getErrors('nov_clause')"
                enable-floating-error
                rows="3"
              />
            </div>

            <div
              v-if="project.isContractor"
              class="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
            >
              <span class="text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.additionalInformationRequired') }}</span>

              <input
                v-model="projectForm.form.nov_additional_info_required"
                name="nov_additional_info_required"
                type="checkbox"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              >
            </div>
          </form>
        </div>
      </div>

      <!-- Variation Pricing Submission Card -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div class="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3">
          <div class="flex items-center space-x-2">
            <div class="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-7 h-7 text-gray-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
</svg>
            </div>

            <h4 class="text-sm font-semibold text-gray-900">
              {{ $t('ProjectSettings.HeadContractTab.variationPricingSubmission') }}
            </h4>
          </div>
        </div>

        <div class="p-4">
          <form
            method="post"
            class="space-y-4"
            @submit.prevent="updateProject"
          >
            <div class="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <span class="text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.noticeRequired') }}</span>

              <input
                v-model="projectForm.form.variation_submission_required"
                name="variation_submission_required"
                type="checkbox"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              >
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">
                {{ $t('ProjectSettings.HeadContractTab.label.noticePeriod') }} ({{ $t('ProjectSettings.HeadContractTab.hint.days') }})
              </label>

              <AppInput
                v-model="projectForm.form.variation_submission_period"
                class="form-input"
                type="number"
                name="variation_submission_period"
                :error="projectForm.getErrors('variation_submission_period')"
              />
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.daysType') }}</label>

              <div class="flex space-x-2">
                <button
                  type="button"
                  class="flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors"
                  :class="[projectForm.form.variation_submission_business_days ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
                  @click="projectForm.form.variation_submission_business_days = true"
                >
                  {{ $t('ProjectSettings.HeadContractTab.button.business') }}
                </button>

                <button
                  type="button"
                  class="flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors"
                  :class="[!projectForm.form.variation_submission_business_days ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
                  @click="projectForm.form.variation_submission_business_days = false"
                >
                  {{ $t('ProjectSettings.HeadContractTab.button.calendar') }}
                </button>
              </div>
            </div>

            <div
              v-if="project.isContractor"
              class="space-y-2"
            >
              <label class="block text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.noticeClause') }}</label>

              <AppTextArea
                v-model="projectForm.form.variation_submission_clause"
                name="variation_submission_clause"
                class="form-input resize-none"
                :error="projectForm.getErrors('variation_submission_clause')"
                enable-floating-error
                rows="3"
              />
            </div>

            <div
              v-if="project.isContractor"
              class="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
            >
              <span class="text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.additionalInformationRequired') }}</span>

              <input
                v-model="projectForm.form.variation_submission_additional_info_required"
                name="variation_submission_additional_info_required"
                type="checkbox"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              >
            </div>
          </form>
        </div>
      </div>

      <!-- Notice of Delay Card -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div class="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3">
          <div class="flex items-center space-x-2">
            <div class="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
              <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    stroke-width="2"
 class="w-4 h-4 text-gray-600">
    <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="2"
        fill="none"
    />

    <path
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M12 6v6l4 2"
    />
</svg>
            </div>

            <h4 class="text-sm font-semibold text-gray-900">
              {{ $t('ProjectSettings.HeadContractTab.noticeOfDelay') }}
            </h4>
          </div>
        </div>

        <div class="p-4">
          <form
            method="post"
            class="space-y-4"
            @submit.prevent="updateProject"
          >
            <div class="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <span class="text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.noticeRequired') }}</span>

              <input
                v-model="projectForm.form.nod_required"
                name="nod_required"
                type="checkbox"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              >
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">
                {{ $t('ProjectSettings.HeadContractTab.label.noticePeriod') }} ({{ $t('ProjectSettings.HeadContractTab.hint.days') }})
              </label>

              <AppInput
                v-model="projectForm.form.nod_period"
                class="form-input"
                type="number"
                name="nod_period"
                :error="projectForm.getErrors('nod_period')"
              />
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.daysType') }}</label>

              <div class="flex space-x-2">
                <button
                  type="button"
                  class="flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors"
                  :class="[projectForm.form.nod_business_days ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
                  @click="projectForm.form.nod_business_days = true"
                >
                  {{ $t('ProjectSettings.HeadContractTab.button.business') }}
                </button>

                <button
                  type="button"
                  class="flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors"
                  :class="[!projectForm.form.nod_business_days ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
                  @click="projectForm.form.nod_business_days = false"
                >
                  {{ $t('ProjectSettings.HeadContractTab.button.calendar') }}
                </button>
              </div>
            </div>

            <div
              v-if="project.isContractor"
              class="space-y-2"
            >
              <label class="block text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.noticeClause') }}</label>

              <AppTextArea
                v-model="projectForm.form.nod_clause"
                name="nod_clause"
                class="form-input resize-none"
                :error="projectForm.getErrors('nod_clause')"
                enable-floating-error
                rows="3"
              />
            </div>

            <div
              v-if="project.isContractor"
              class="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
            >
              <span class="text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.additionalInformationRequired') }}</span>

              <input
                v-model="projectForm.form.nod_additional_info_required"
                name="nod_additional_info_required"
                type="checkbox"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              >
            </div>
          </form>
        </div>
      </div>

      <!-- Extension of Time Submission Card -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div class="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3">
          <div class="flex items-center space-x-2">
            <div class="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
              <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4 text-gray-600">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
</svg>
            </div>

            <h4 class="text-sm font-semibold text-gray-900">
              {{ $t('ProjectSettings.HeadContractTab.extensionOfTimeSubmission') }}
            </h4>
          </div>
        </div>

        <div class="p-4">
          <form
            method="post"
            class="space-y-4"
            @submit.prevent="updateProject"
          >
            <div class="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <span class="text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.noticeRequired') }}</span>

              <input
                v-model="projectForm.form.delay_submission_required"
                name="delay_submission_required"
                type="checkbox"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              >
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">
                {{ $t('ProjectSettings.HeadContractTab.label.noticePeriod') }} ({{ $t('ProjectSettings.HeadContractTab.hint.days') }})
              </label>

              <AppInput
                v-model="projectForm.form.delay_submission_period"
                class="form-input"
                type="number"
                name="delay_submission_period"
                :error="projectForm.getErrors('delay_submission_period')"
              />
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.daysType') }}</label>

              <div class="flex space-x-2">
                <button
                  type="button"
                  class="flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors"
                  :class="[projectForm.form.delay_submission_business_days ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
                  @click="projectForm.form.delay_submission_business_days = true"
                >
                  {{ $t('ProjectSettings.HeadContractTab.button.business') }}
                </button>

                <button
                  type="button"
                  class="flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors"
                  :class="[!projectForm.form.delay_submission_business_days ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
                  @click="projectForm.form.delay_submission_business_days = false"
                >
                  {{ $t('ProjectSettings.HeadContractTab.button.calendar') }}
                </button>
              </div>
            </div>

            <div
              v-if="project.isContractor"
              class="space-y-2"
            >
              <label class="block text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.noticeClause') }}</label>

              <AppTextArea
                v-model="projectForm.form.delay_submission_clause"
                name="delay_submission_clause"
                class="form-input resize-none"
                :error="projectForm.getErrors('delay_submission_clause')"
                enable-floating-error
                rows="3"
              />
            </div>

            <div
              v-if="project.isContractor"
              class="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
            >
              <span class="text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.additionalInformationRequired') }}</span>

              <input
                v-model="projectForm.form.delay_submission_additional_info_required"
                name="delay_submission_additional_info_required"
                type="checkbox"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              >
            </div>
          </form>
        </div>
      </div>

      <!-- Raise CEN to VO/Delay Card -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div class="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3">
          <div class="flex items-center space-x-2">
            <div class="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-4 h-4 text-gray-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
            </div>

            <h4 class="text-sm font-semibold text-gray-900">
              {{ $t('ProjectSettings.HeadContractTab.raiseCenToVoDelay') }}
            </h4>
          </div>
        </div>

        <div class="p-4">
          <form
            method="post"
            class="space-y-4"
            @submit.prevent="updateProject"
          >
            <div class="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <span class="text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.noticeRequired') }}</span>

              <input
                v-model="projectForm.form.cen_raise_required"
                name="cen_raise_required"
                type="checkbox"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              >
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">
                {{ $t('ProjectSettings.HeadContractTab.label.noticePeriod') }} ({{ $t('ProjectSettings.HeadContractTab.hint.days') }})
              </label>

              <AppInput
                v-model="projectForm.form.cen_raise_period"
                class="form-input"
                type="number"
                name="cen_raise_period"
                :error="projectForm.getErrors('cen_raise_period')"
              />
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">{{ $t('ProjectSettings.HeadContractTab.label.daysType') }}</label>

              <div class="flex space-x-2">
                <button
                  type="button"
                  class="flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors"
                  :class="[projectForm.form.cen_raise_business_days ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
                  @click="projectForm.form.cen_raise_business_days = true"
                >
                  {{ $t('ProjectSettings.HeadContractTab.button.business') }}
                </button>

                <button
                  type="button"
                  class="flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors"
                  :class="[!projectForm.form.cen_raise_business_days ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
                  @click="projectForm.form.cen_raise_business_days = false"
                >
                  {{ $t('ProjectSettings.HeadContractTab.button.calendar') }}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { dateForDateInput, Holiday, MONTHS } from '@/helpers';
import { useClaimsStore } from '@/store/claims';
import { useProjectsStore } from '@/store/projects';
import { useUserStore } from '@/store/user';
import { DateTime } from 'luxon';
import Swal from 'sweetalert2';
import { computed, nextTick, ref, watch } from 'vue';

const props = defineProps<{
  projectId: string;
}>();

const emits = defineEmits<{
  (e: 'dirty');
  (e: 'saved');
}>();

const projectsStore = useProjectsStore();

const projectForm = useFormData({
  contractor_email: '',
  pqs_email: '',
  superintendent_email: '',
  client_emails: [] as string[],
  contract_start_date: '',
  contract_end_date: '',
  claim_start: '',
  commentary_lock: 0,
  nov_clause: '',
  nov_required: false,
  nov_additional_info_required: false,
  nov_period: 0,
  nov_business_days: false,
  variation_submission_business_days: false,
  variation_submission_clause: '',
  variation_submission_required: false,
  variation_submission_additional_info_required: false,
  variation_submission_period: 0,
  nod_clause: '',
  nod_required: false,
  nod_additional_info_required: false,
  nod_period: 0,
  nod_business_days: false,
  delay_submission_clause: '',
  delay_submission_required: false,
  delay_submission_additional_info_required: false,
  delay_submission_period: 0,
  delay_submission_business_days: false,
  cen_raise_required: false,
  cen_raise_period: 0,
  cen_raise_business_days: false,
  client_details: '',
  hired_company_details: '',
  financier_details: '',
  site_details: '',
  holidays: ([] as Holiday[]),
  claim_clause: '',
  certificate_clause: '',
  final_account_clause: '',
  claim_margin_percent: 10,
  apply_tax: false,
  tax_percent: undefined,
  performance_security: 'retention',
  claim_retention_percent: 10,
  total_retention_percent: 5,
  show_ve_items_on_claims: false,
  allow_certified_over_budget: false,
});

const project = computed(() => {
  return projectsStore.project;
});

watch(project, (newValue) => {
  if(newValue) {
    projectForm.setData(newValue.head_contract_settings, ['contract_end_date', 'contract_start_date', 'claim_start']);

    projectForm.form.claim_start = dateForDateInput(newValue.claim_start);
    projectForm.form.contract_end_date = dateForDateInput(newValue.contract_end_date);
    projectForm.form.contract_start_date = dateForDateInput(newValue.contract_start_date);

    projectForm.resetErrors();

    nextTick(() => {
      projectForm.resetDirtyStatus();
    });
  }
}, { immediate: true });

watch(projectForm.isDirty, (newValue) => {
  if(newValue) {
    emits('dirty');
  } else {
    emits('saved');
  }
});

const updateProjectAction = useStoreApiAction(projectsStore.updateHeadContractSettings);

const updateProject = () => {
  projectForm.resetErrors();

  updateProjectAction.request(props.projectId, projectForm.form).then(() => {
    Swal.fire({
      icon: 'success',
      title: 'Head Contract Settings Updated.',
    });
  }).catch((error) => {
    projectForm.setErrors(error.data);
  });
};

const userStore = useUserStore();

const contractLength = computed(() => {
  if(projectForm.form.contract_end_date && projectForm.form.contract_start_date) {
    const contractEndDate = DateTime.fromFormat(projectForm.form.contract_end_date, 'yyyy-MM-dd').endOf('month');
    const contractStartDate = DateTime.fromFormat(projectForm.form.contract_start_date, 'yyyy-MM-dd').startOf('month');

    if(contractEndDate.isValid && contractStartDate.isValid) {
      return Math.round(Math.abs(contractStartDate.diff(contractEndDate, ['months']).months));
    }
  }

  return undefined;
});

const holidayType = ref<'single' | 'range'>('single');
const holidayRecurring = ref(false);
const holidayDay = ref<number | undefined>();
const holidayMonth = ref<number | undefined>();
const holidayYear = ref<number | undefined>();
const holidayLabel = ref<string | undefined>();
const holidayStartDate = ref<string | undefined>();
const holidayEndDate = ref<string | undefined>();

watch(holidayRecurring, (newValue) => {
  if(newValue) {
    holidayYear.value = undefined;
  }
});

watch(holidayType, () => {
  // Reset form fields when switching types
  holidayDay.value = undefined;
  holidayMonth.value = undefined;
  holidayYear.value = undefined;
  holidayStartDate.value = undefined;
  holidayEndDate.value = undefined;
  holidayRecurring.value = false;
});

const canAddHoliday = computed(() => {
  if(!holidayLabel.value) {
    return false;
  }

  if(holidayType.value === 'range') {
    return !!(holidayStartDate.value && holidayEndDate.value);
  }

  // Single day holiday
  if(holidayRecurring.value) {
    return !!(holidayDay.value && holidayMonth.value);
  }

  return !!(holidayDay.value && holidayMonth.value && holidayYear.value);
});

const addHoliday = () => {
  if(canAddHoliday.value) {
    if(holidayType.value === 'range') {
      projectForm.form.holidays.push({
        label: holidayLabel.value,
        type: 'range',
        start_date: holidayStartDate.value,
        end_date: holidayEndDate.value,
      });
    } else {
      // Single day holiday
      const holiday: Holiday = {
        label: holidayLabel.value!,
        type: 'single',
        day: holidayDay.value,
        month: holidayMonth.value,
      };

      if(!holidayRecurring.value && holidayYear.value) {
        holiday.year = holidayYear.value;
      }

      projectForm.form.holidays.push(holiday);
    }

    // Reset form
    holidayLabel.value = undefined;
    holidayDay.value = undefined;
    holidayMonth.value = undefined;
    holidayYear.value = undefined;
    holidayStartDate.value = undefined;
    holidayEndDate.value = undefined;
    holidayRecurring.value = false;
    holidayType.value = 'single';
  }
};

const removeHoliday = (index: number) => {
  projectForm.form.holidays.splice(index, 1);
};

// Client email management
const clientEmailInput = ref<string>('');

const canAddClientEmail = computed(() => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return !!(clientEmailInput.value && emailRegex.test(clientEmailInput.value));
});

const addClientEmail = () => {
  if(canAddClientEmail.value) {
    projectForm.form.client_emails.push(clientEmailInput.value);
    clientEmailInput.value = '';
  }
};

const removeClientEmail = (index: number) => {
  projectForm.form.client_emails.splice(index, 1);
};

const claimsStore = useClaimsStore();
const exportRetentionReleaseAction = useStoreApiAction(claimsStore.exportRetentionRelease);

const exportRetentionRelease = () => {
  Swal.fire({
    title: 'Select Retention Release Period',
    input: 'select',
    inputOptions: {
      dlp: 'DLP',
      pc: 'PC',
    },

    inputPlaceholder: 'Select a period',
    showCancelButton: true,
    inputValidator: (value) => {
      if(!value) {
        return 'Please select a period';
      }
    },
  }).then((result) => {
    if(!result.isConfirmed) {
      return;
    }

    exportRetentionReleaseAction.request(props.projectId, { period: result.value }).then(() => {
      Swal.fire({
        icon: 'success',
        title: 'Retention Release Certificate exported.',
        text: 'Retention Release Certificate is being exported to PDF and will be sent to your email when it is ready.',
      });
    });
  });
};

const isProjectCompleted = computed(() => {
  return projectsStore.project?.contractEndDate && (projectsStore.project?.contractEndDate < new Date());
});
</script>

<style scoped></style>
